import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  runTransaction,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';

// Username validation rules
const USERNAME_MIN_LENGTH = 3;
const USERNAME_MAX_LENGTH = 20;
const USERNAME_REGEX = /^[a-zA-Z0-9_]+$/;
const RESERVED_USERNAMES = ['admin', 'moderator', 'mod', 'hyzone', 'system', 'support', 'help'];

// How often username can be changed (30 days in milliseconds)
const USERNAME_CHANGE_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;

export interface UsernameValidationResult {
  isValid: boolean;
  error?: string;
}

export interface UsernameAvailabilityResult {
  isAvailable: boolean;
  error?: string;
}

/**
 * Validates username format (does not check availability)
 */
export function validateUsername(username: string): UsernameValidationResult {
  const trimmed = username.trim().toLowerCase();

  if (trimmed.length < USERNAME_MIN_LENGTH) {
    return {
      isValid: false,
      error: `Username must be at least ${USERNAME_MIN_LENGTH} characters`,
    };
  }

  if (trimmed.length > USERNAME_MAX_LENGTH) {
    return {
      isValid: false,
      error: `Username must be at most ${USERNAME_MAX_LENGTH} characters`,
    };
  }

  if (!USERNAME_REGEX.test(trimmed)) {
    return {
      isValid: false,
      error: 'Username can only contain letters, numbers, and underscores',
    };
  }

  if (RESERVED_USERNAMES.includes(trimmed)) {
    return {
      isValid: false,
      error: 'This username is reserved',
    };
  }

  return { isValid: true };
}

/**
 * Checks if a username is available
 */
export async function checkUsernameAvailability(
  username: string,
  currentUserId?: string
): Promise<UsernameAvailabilityResult> {
  const normalizedUsername = username.trim().toLowerCase();

  // First validate format
  const validation = validateUsername(normalizedUsername);
  if (!validation.isValid) {
    return { isAvailable: false, error: validation.error };
  }

  // Check if username exists in usernames collection
  const usernameRef = doc(db, 'usernames', normalizedUsername);
  const usernameSnap = await getDoc(usernameRef);

  if (usernameSnap.exists()) {
    const data = usernameSnap.data();
    // If the username belongs to the current user, it's "available" for them
    if (currentUserId && data.userId === currentUserId) {
      return { isAvailable: true };
    }
    return { isAvailable: false, error: 'Username is already taken' };
  }

  return { isAvailable: true };
}

/**
 * Checks if the user can change their username (30-day cooldown)
 */
export function canChangeUsername(usernameChangedAt?: Timestamp): boolean {
  if (!usernameChangedAt) {
    return true; // Never set, can set it
  }

  const lastChanged = usernameChangedAt.toMillis();
  const now = Date.now();
  return now - lastChanged >= USERNAME_CHANGE_COOLDOWN_MS;
}

/**
 * Gets the remaining time until username can be changed again
 */
export function getTimeUntilUsernameChange(usernameChangedAt?: Timestamp): number {
  if (!usernameChangedAt) {
    return 0;
  }

  const lastChanged = usernameChangedAt.toMillis();
  const now = Date.now();
  const elapsed = now - lastChanged;
  const remaining = USERNAME_CHANGE_COOLDOWN_MS - elapsed;

  return Math.max(0, remaining);
}

/**
 * Formats remaining time as human-readable string
 */
export function formatTimeRemaining(ms: number): string {
  if (ms <= 0) return '';

  const days = Math.ceil(ms / (24 * 60 * 60 * 1000));
  if (days === 1) return '1 day';
  return `${days} days`;
}

/**
 * Generates a fallback username from email
 * Format: emailPrefix + 3 random digits
 */
export function generateFallbackUsername(email: string): string {
  const emailPrefix = email.split('@')[0].replace(/[^a-zA-Z0-9_]/g, '').toLowerCase();
  const randomDigits = Math.floor(Math.random() * 900 + 100).toString(); // 100-999

  let username = emailPrefix + randomDigits;

  // Ensure it meets minimum length
  if (username.length < USERNAME_MIN_LENGTH) {
    username = username.padEnd(USERNAME_MIN_LENGTH, '0');
  }

  // Truncate if too long
  if (username.length > USERNAME_MAX_LENGTH) {
    username = username.slice(0, USERNAME_MAX_LENGTH - 3) + randomDigits;
  }

  return username;
}

/**
 * Sets or updates a user's username using a transaction
 * This ensures atomicity between the user document and usernames collection
 */
export async function setUsername(
  userId: string,
  newUsername: string,
  currentUsername?: string
): Promise<{ success: boolean; error?: string }> {
  const normalizedNewUsername = newUsername.trim().toLowerCase();

  // Validate format
  const validation = validateUsername(normalizedNewUsername);
  if (!validation.isValid) {
    return { success: false, error: validation.error };
  }

  try {
    await runTransaction(db, async (transaction) => {
      const userRef = doc(db, 'users', userId);
      const newUsernameRef = doc(db, 'usernames', normalizedNewUsername);

      // Check if new username is taken
      const newUsernameDoc = await transaction.get(newUsernameRef);
      if (newUsernameDoc.exists() && newUsernameDoc.data().userId !== userId) {
        throw new Error('Username is already taken');
      }

      // If user has an existing username, delete the old entry
      if (currentUsername && currentUsername !== normalizedNewUsername) {
        const oldUsernameRef = doc(db, 'usernames', currentUsername.toLowerCase());
        transaction.delete(oldUsernameRef);
      }

      // Create new username entry
      transaction.set(newUsernameRef, {
        userId,
        createdAt: serverTimestamp(),
      });

      // Update user document
      transaction.update(userRef, {
        username: normalizedNewUsername,
        usernameChangedAt: serverTimestamp(),
      });
    });

    return { success: true };
  } catch (error: any) {
    console.error('Error setting username:', error);
    return {
      success: false,
      error: error.message || 'Failed to set username. Please try again.',
    };
  }
}

/**
 * Gets user ID by username (for profile lookups)
 */
export async function getUserIdByUsername(username: string): Promise<string | null> {
  const normalizedUsername = username.trim().toLowerCase();
  const usernameRef = doc(db, 'usernames', normalizedUsername);
  const usernameSnap = await getDoc(usernameRef);

  if (usernameSnap.exists()) {
    return usernameSnap.data().userId;
  }

  return null;
}
